import { BatchRequest, BatchResponse } from 'algoliasearch'

/**
 * Algolia REST API fetch helper.
 *
 * @see https://www.algolia.com/doc/api-reference/rest-api/
 */
export const algoliaFetch = async <T extends object, R extends object>({
  path,
  body,
  // Time-based revalidation in seconds; disabled by default for Algolia analytics/personalisation.
  revalidate = 0,
  // Cache options; disabled by default for Algolia analytics/personalisation.
  // @see https://nextjs.org/docs/app/api-reference/functions/fetch#optionscache
  cache = 'no-cache',
}: {
  path: string
  body: T
  revalidate?: number | false
  cache?: RequestCache
}): Promise<R | undefined> => {
  const appId = process.env.NEXT_PUBLIC_ALGOLIA_APPLICATION_ID || ''
  const apiKey = process.env.NEXT_PUBLIC_ALGOLIA_SEARCH_API_KEY || ''
  const baseUrl = 'events' in body ? 'https://insights.algolia.io' : `https://${appId}-dsn.algolia.net`
  const url = `${baseUrl}${path}`

  const headers = {
    'X-Algolia-API-Key': apiKey,
    'X-Algolia-Application-Id': appId,
    'Content-Type': 'application/json',
  }

  const response = await fetch(url, {
    headers,
    cache,
    method: 'POST',
    body: JSON.stringify(body),
    next: {
      revalidate,
    },
  })

  if (!response.ok) {
    const message = `Algolia ${url} request error: ${response.statusText}`
    console.error(message, await response.json())

    throw new Error(message)
  }

  return response.json()
}

export type { BatchRequest, BatchResponse }
